"use client"

import { Children, useState } from "react"

type Props = {
  /** Pre-rendered product cards (Server Components) passed from the template. */
  children: React.ReactNode
  /** How many to show before the first "Load more". */
  initial?: number
  /** How many more each click reveals. */
  step?: number
}

/**
 * Progressive reveal grid. The full set of cards is rendered on the server and
 * handed in as `children`; this client wrapper only mounts the first `initial`
 * and reveals `step` more per click — no extra network, instant reveal, all
 * products still in the RSC payload (SEO-safe). Remount (via a `key` on the
 * parent) resets the visible count when filters/sort change.
 */
export default function LoadMoreGrid({
  children,
  initial = 30,
  step = 30,
}: Props) {
  const items = Children.toArray(children)
  const [visible, setVisible] = useState(Math.min(initial, items.length))
  const shown = items.slice(0, visible)
  const remaining = items.length - visible

  return (
    <>
      {/* 4 cards per line on desktop (matches the categories page); tighter
          gutters make each card ~3% larger than a 4-up grid with wide gaps. */}
      <div
        className="grid grid-cols-2 gap-x-3 gap-y-8 small:grid-cols-4 small:gap-x-3 small:gap-y-10"
        data-testid="products-list"
      >
        {shown}
      </div>

      {remaining > 0 && (
        <div className="mt-12 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => setVisible((v) => Math.min(v + step, items.length))}
            className="alif-label rounded-sm border border-alif-ink px-10 py-3.5 text-alif-ink transition-colors hover:border-alif-green hover:bg-alif-green hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-alif-green focus-visible:ring-offset-2"
            data-testid="load-more"
          >
            Load more
          </button>
          <span className="text-[12px] text-alif-muted">
            {visible} of {items.length}
          </span>
        </div>
      )}
    </>
  )
}
